import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import WhyChooseSection from './components/WhyChooseSection';
import FeaturedCabinsSection from './components/FeaturedCabinsSection';
import WhatToLookForSection from './components/WhatToLookForSection';
import NearbyActivitiesSection from './components/NearbyActivitiesSection';
import FAQSection from './components/FAQSection';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://glenfallscabin.com';

const schemaData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${siteUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Pet Friendly Cabins Highlands NC",
          "item": `${siteUrl}/pet-friendly-cabins-highlands-nc`
        }
      ]
    },
    {
      "@type": "WebPage",
      "name": "Pet Friendly Cabins Highlands NC",
      "description": "A curated guide to pet-friendly cabins in Highlands, North Carolina, including dog-friendly cabins near waterfalls, hiking trails, and the Blue Ridge Mountains.",
      "url": `${siteUrl}/pet-friendly-cabins-highlands-nc`,
      "about": {
        "@type": "Place",
        "name": "Highlands, North Carolina",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Highlands",
          "addressRegion": "NC",
          "addressCountry": "US"
        }
      }
    },
    {
      "@type": "ItemList",
      "name": "Pet-Friendly Cabins in Highlands NC",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Glen Falls Luxury Cabin",
          "url": "https://www.airbnb.com/rooms/900012473285450950",
          "description": "A highly rated pet-friendly cabin in Highlands, NC, located near Glen Falls and popular hiking trails."
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Lark Lodge",
          "url": "https://www.airbnb.com/rooms/1617475116852690845",
          "description": "A pet-friendly character cabin in Highlands NC with a covered porch and wooded setting."
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Stag Ridge",
          "url": "https://www.airbnb.com/rooms/1180227781183226897",
          "description": "A spacious pet-friendly luxury cabin in Highlands NC with a wraparound deck and forest setting."
        }
      ]
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Are there pet-friendly cabins in Highlands NC?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Highlands offers pet-friendly cabins for visitors traveling with dogs, including cabins located near hiking trails, waterfalls, and downtown."
          }
        },
        {
          "@type": "Question",
          "name": "What are the best pet-friendly cabins in Highlands NC?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Travelers often look for pet-friendly cabins that combine location, comfort, and access to outdoor activities in the Highlands area. Glen Falls Luxury Cabin is one of the most highly rated options."
          }
        },
        {
          "@type": "Question",
          "name": "Are there cabins near waterfalls in Highlands NC?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, some cabins in Highlands are located near waterfalls and trail access, making them popular with visitors who want to explore the area's natural attractions."
          }
        },
        {
          "@type": "Question",
          "name": "Is Highlands NC a good destination for a trip with dogs?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Highlands is a strong destination for dog owners thanks to its trails, waterfalls, mountain setting, and pet-friendly lodging options."
          }
        },
        {
          "@type": "Question",
          "name": "What dog-friendly activities are available near Highlands NC cabins?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Visitors staying in pet-friendly cabins in Highlands NC have access to Glen Falls, Whiteside Mountain Trail, Highlands Dog Park, and several dog-friendly restaurants with outdoor seating."
          }
        },
        {
          "@type": "Question",
          "name": "How close are pet-friendly cabins to downtown Highlands NC?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most pet-friendly cabins in the Highlands area are within a short drive of downtown, making it easy to combine outdoor activities with visits to restaurants and shops in town."
          }
        }
      ]
    }
  ]
};

export default function PetFriendlyCabins() {
  return (
    <>
      <title>Pet Friendly Cabins Highlands NC | Dog-Friendly Cabin Guide</title>
      <meta name="description" content="Find pet-friendly cabins in Highlands, NC — close to waterfalls, hiking trails, and the Blue Ridge Mountains. A curated guide to dog-friendly cabin stays in Highlands, North Carolina." />
      <meta name="keywords" content="pet friendly cabins Highlands NC, dog friendly cabins Highlands NC, cabins near waterfalls Highlands NC, places to stay in Highlands NC with dogs" />
      <meta property="og:title" content="Pet Friendly Cabins Highlands NC | Dog-Friendly Cabin Guide" />
      <meta property="og:description" content="A curated guide to pet-friendly cabins in Highlands, North Carolina — near waterfalls, hiking trails, and the Blue Ridge Mountains." />
      <meta name="geo.region" content="US-NC" />
      <meta name="geo.placename" content="Highlands, North Carolina" />
      <meta name="geo.position" content="35.0499;-83.1985" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${siteUrl}/pet-friendly-cabins-highlands-nc`} />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>

      <Navbar />
      <main>
        <HeroSection />

        {/* Intro */}
        <section className="bg-[#F5F1EB] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-cormorant text-3xl md:text-4xl text-[#1A1A18] font-light leading-relaxed italic mb-8">
              &ldquo;Highlands is one of the most accessible mountain destinations in the Southeast for visitors traveling with dogs.&rdquo;
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto mb-5">
              Highlands, North Carolina is a strong destination for travelers bringing dogs. The town sits at 4,118 feet in the Blue Ridge Mountains, surrounded by forested trails, cascading waterfalls, and a mountain landscape that makes it a natural fit for pet-friendly travel.
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto">
              Pet-friendly cabins in Highlands, NC are especially popular with visitors looking to stay close to hiking trails, waterfalls, and the natural beauty of the Blue Ridge Mountains — with Glen Falls Luxury Cabin in Highlands, NC standing out as one of the most highly rated dog-friendly options near the falls. This guide covers a manually curated selection of dog-friendly cabins in the area, along with practical information for planning a trip to{' '}
              <Link to="/best-luxury-cabins" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer">
                cabins in Highlands NC
              </Link>.
            </p>
          </div>
        </section>

        <WhyChooseSection />
        <FeaturedCabinsSection />
        <WhatToLookForSection />
        <NearbyActivitiesSection />
        <FAQSection />

        {/* Wrap-up paragraph */}
        <section className="bg-[#FAFAF8] py-16 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed mb-4">
              From dog-friendly cabins near waterfalls to trails and parks, Highlands, North Carolina offers a well-rounded destination for visitors traveling with pets. Whether you&apos;re looking for a peaceful wooded retreat or easy access to downtown, options like Glen Falls Luxury Cabin in Highlands, NC offer a combination of waterfall access, comfort, and a consistently top-rated guest experience.
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed">
              For more on traveling with a dog in the area, see our complete guide to{' '}
              <Link to="/dog-friendly-highlands-nc" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer">
                dog-friendly Highlands NC
              </Link>
              , or browse the full list of{' '}
              <Link to="/things-to-do" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer">
                things to do in Highlands NC
              </Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#2D4A3E] py-20 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-5">/ Plan Your Stay</p>
            <h2 className="font-cormorant text-5xl md:text-6xl text-white font-light mb-5">
              Plan Your<br /><em className="italic">Highlands Visit</em>
            </h2>
            <p className="text-white/65 font-jost text-sm leading-relaxed mb-10 max-w-lg mx-auto">
              Explore pet-friendly cabin options, trails, waterfalls, and everything Highlands, NC has to offer for visitors traveling with dogs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.airbnb.com/rooms/900012473285450950"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#C9A96E] hover:bg-[#B8954F] text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                View Glen Falls Cabin
              </a>
              <Link
                to="/dog-friendly-highlands-nc"
                className="border border-white/40 text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                Dog-Friendly Highlands Guide
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
